import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactCardFlip from 'react-card-flip';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { anonymizeFrontText } from './frontText';

// ---------------------------------------------------------------------------
// Wahlkompass U16 Berlin 2026
// Ein Datensatz (src/data.json): 25 Wahlpruefstein-Fragen x 14 Parteien.
// Anzeige-Reihenfolge = config.statementIndicesToShow (Original-Datenindizes).
// Bewertungen sind an der Daten-ID verankert (nicht an List-Positionen).
// ---------------------------------------------------------------------------

const fetchWithErrorHandling = async (url, options) => {
  const response = await fetch(url, options);
  if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
  return response.json();
};

// Kleiner Helfer: aus config statementIndicesToShow die Anzeige-Liste bauen.
// Unbekannte/doppelte IDs werden gefiltert; Reihenfolge der config bleibt.
const buildShownStatements = (allData, indicesToShow) => {
  const byId = {};
  allData.forEach((s) => { byId[s.ID] = s; });
  const seen = new Set();
  const list = [];
  (indicesToShow || []).forEach((id) => {
    if (!seen.has(id) && byId[id] !== undefined) {
      seen.add(id);
      list.push(byId[id]);
    }
  });
  return list;
};

const shuffleArray = (array) => {
  const a = [...array];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Parteien-Filter (config.partiesToShow): fehlt/leer => alle Parteien
const isPartyShown = (config, party) => {
  const list = config && Array.isArray(config.partiesToShow) ? config.partiesToShow : null;
  return !list || list.length === 0 || list.includes(party);
};

// Initialen-Platzhalter fuer Parteien ohne Logo-Datei
const partyInitials = (name) =>
  name
    .replace(/BÜNDNIS 90\/DIE GRÜNEN/, 'GRÜNE')
    .replace(/PARTEI MENSCH KLIMA TIERSCHUTZ/, 'TIERSCHUTZ')
    .replace(/Piratenpartei Deutschland/, 'PIRATEN')
    .split(/[\s/]+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 4);

const PartyLogo = ({ party, logoPath, sizeClass }) => {
  const [broken, setBroken] = useState(false);
  if (!logoPath || broken) {
    return (
      <span
        className={`inline-flex items-center justify-center rounded-full bg-gray-200 text-gray-600 font-bold ${sizeClass}`}
        title={party}
      >
        {partyInitials(party)}
      </span>
    );
  }
  return (
    <img
      src={logoPath}
      alt={party}
      title={party}
      className={`inline-block object-contain ${sizeClass}`}
      onError={() => setBroken(true)}
    />
  );
};

const WaterfallNavigation = ({ statements, currentPos, setCurrentPos, indicesToShow }) => (
  <div className="flex-none overflow-y-auto border-l-2 border-gray-200" style={{ maxHeight: '45vh' }}>
    {statements.map((statement, pos) => {
      const isEnabled = indicesToShow.includes(statement.ID);
      return (
        <div
          key={statement.ID}
          className={`p-2 cursor-pointer ${pos === currentPos ? 'bg-blue-100' : isEnabled ? 'bg-green-200' : 'bg-red-200'}`}
          onClick={() => setCurrentPos(pos)}
        >
          <div className="text-sm">{pos + 1}. {statement.Label}</div>
        </div>
      );
    })}
  </div>
);

const PartyFilter = ({ parties, selected, onToggle }) => (
  <div className="flex-none border-t-2 border-gray-200 p-2 overflow-y-auto" style={{ maxHeight: '40vh' }}>
    <div className="text-sm font-semibold mb-1">
      Parteien anzeigen ({selected.length}/{parties.length})
    </div>
    {parties.map((p) => (
      <label key={p} className="flex items-start gap-1 py-0.5 cursor-pointer text-sm">
        <input type="checkbox" className="mt-0.5 flex-none" checked={selected.includes(p)} onChange={() => onToggle(p)} />
        <span className="break-words leading-snug">{p}</span>
      </label>
    ))}
  </div>
);

const App = () => {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [started, setStarted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // alle Fragen (Datenreihenfolge) + angezeigte Fragen (config-Reihenfolge)
  const [allData, setAllData] = useState([]);
  const [shown, setShown] = useState([]);
  const [currentPos, setCurrentPos] = useState(0);
  const [votes, setVotes] = useState({});      // { dataId: { party: 'up'|'neutral'|'down' } }
  const [flipped, setFlipped] = useState({});  // { dataId: { party: true } }
  const [shuffled, setShuffled] = useState({}); // { dataId: [{response, color}] }
  const searchParams = new URLSearchParams(window.location.search);

  // Parteien-Filter: Signatur der partiesToShow-Liste (Shuffle-Cache je Filterstand)
  const partySig = config && Array.isArray(config.partiesToShow)
    ? config.partiesToShow.join('|')
    : '*';

  // Alle Parteinamen aus den Daten (Reihenfolge des ersten Vorkommens)
  const partyNames = useMemo(() => {
    const seen = [];
    const map = {};
    (allData || []).forEach((s) => {
      (s.Responses || []).forEach((r) => {
        if (!map[r.Party]) {
          map[r.Party] = true;
          seen.push(r.Party);
        }
      });
    });
    return seen;
  }, [allData]);

  // Sprachausgabe: EIN gemeinsamer Audio-Player (immer nur ein Ton gleichzeitig)
  const audioRef = useRef(null);
  const [playingKey, setPlayingKey] = useState(null);

  const toggleAudio = useCallback((key, url) => {
    const a = audioRef.current;
    if (!a) return;
    const same = playingKey === key;
    a.pause();
    a.currentTime = 0;
    if (same) {
      setPlayingKey(null); // Stopp
      return;
    }
    a.src = url;
    a.play().then(() => setPlayingKey(key)).catch(() => setPlayingKey(null));
  }, [playingKey]);

  // Barrierefreiheit: Ansagen fuer Screenreader (aria-live)
  const [announce, setAnnounce] = useState('');
  const announceText = useCallback((msg) => setAnnounce(msg), []);

  // Schriftgroesse (+/-), wirkt auf rem-Basis -> gesamte Schrift skaliert
  const [fontPct, setFontPct] = useState(() => {
    try {
      const v = Number(window.localStorage.getItem('wk_font_pct'));
      return v >= 80 && v <= 150 ? v : 100;
    } catch (e) {
      return 100;
    }
  });
  useEffect(() => {
    document.documentElement.style.fontSize = `${fontPct}%`;
    try {
      window.localStorage.setItem('wk_font_pct', String(fontPct));
    } catch (e) { /* ignore */ }
    return () => { document.documentElement.style.fontSize = ''; };
  }, [fontPct]);
  const bumpFont = useCallback((delta) => {
    setFontPct((p) => Math.min(150, Math.max(80, p + delta)));
  }, []);

  // Bei Fragenwechsel/Neustart laufende Wiedergabe stoppen
  useEffect(() => {
    const a = audioRef.current;
    if (a && playingKey) {
      a.pause();
      a.currentTime = 0;
      setPlayingKey(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPos, started]);

  useEffect(() => {
    if (searchParams.has('edit')) setIsEditing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const data = await fetchWithErrorHandling('/api/config');
        setConfig(data);
        document.title = data.textDefinitions.baseTitle;
      } catch (e) {
        console.error(e);
        setError('Error loading configuration. Please try again later.');
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchWithErrorHandling('/api/data');
        setAllData(data);
      } catch (e) {
        console.error(e);
        setError('Error loading data. Please try again later.');
      }
    };
    fetchData();
  }, []);

  // Anzeige-Liste: Edit-Modus = alle Fragen (Datenreihenfolge),
  // sonst = config-Reihenfolge. Position nur klemmen, nie hart zuruecksetzen.
  useEffect(() => {
    if (!config) return;
    setShown(isEditing ? allData : buildShownStatements(allData, config.statementIndicesToShow));
  }, [config, allData, isEditing]);

  useEffect(() => {
    const len = list.length;
    setCurrentPos((p) => (len ? Math.min(p, len - 1) : 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown.length, allData.length, isEditing]);

  const list = isEditing ? allData : shown;
  const currentStatement = list[currentPos];
  const atStats = !currentStatement; // nach der letzten Frage

  // Fortschritt durch die angezeigten Fragen (0..100 %)
  const progressPct = !started ? 0 : atStats ? 100 : Math.round(((currentPos + 1) / Math.max(1, list.length)) * 100);

  useEffect(() => {
    if (config) {
      let title = config.textDefinitions.baseTitle;
      if (!started) title += ` - ${config.textDefinitions.startPageTitle}`;
      else if (atStats) title += ` - ${config.textDefinitions.statsPageTitle}`;
      else if (currentStatement) title += ` - ${currentStatement.Label}`;
      document.title = title;
    }
  }, [config, started, atStats, currentStatement]);

  const ensureShuffled = useCallback((statement) => {
    if (!statement || !statement.Responses || !config) return null;
    const key = `${statement.ID}::${partySig}`;
    if (shuffled[key]) return shuffled[key];
    const pool = statement.Responses
      .map((r, oi) => ({ response: r, origIdx: oi }))
      .filter((x) => isPartyShown(config, x.response.Party));
    const responses = shuffleArray(pool);
    const colors = shuffleArray([...config.colorPalette.pastelBackground]);
    const withColors = responses.map((x, i) => ({
      response: x.response,
      origIdx: x.origIdx,
      color: colors[i % colors.length],
    }));
    setShuffled((prev) => ({ ...prev, [key]: withColors }));
    return withColors;
  }, [shuffled, config, partySig]);

  useEffect(() => {
    if (currentStatement) ensureShuffled(currentStatement);
  }, [currentStatement, ensureShuffled]);

  const voteLabelMap = { up: 'find ich gut', neutral: 'unentschieden', down: 'find ich doof' };

  const handleVote = useCallback((party, voteType) => {
    const id = currentStatement.ID;
    setVotes((prev) => ({
      ...prev,
      [id]: { ...prev[id], [party]: voteType },
    }));
    setFlipped((prev) => ({
      ...prev,
      [id]: { ...prev[id], [party]: true },
    }));
    announceText(`Bewertung gespeichert: ${party} – ${voteLabelMap[voteType]}.`);
  }, [currentStatement, announceText, voteLabelMap]);

  const undoVote = useCallback((party) => {
    const id = currentStatement.ID;
    setVotes((prev) => {
      const next = { ...prev };
      if (next[id]) {
        const copy = { ...next[id] };
        delete copy[party];
        next[id] = copy;
      }
      return next;
    });
    setFlipped((prev) => {
      const next = { ...prev };
      if (next[id]) {
        const copy = { ...next[id] };
        delete copy[party];
        next[id] = copy;
      }
      return next;
    });
    announceText(`Bewertung für ${party} zurückgenommen.`);
  }, [currentStatement, announceText]);

  const navigate = useCallback((direction) => {
    setCurrentPos((prev) => {
      if (direction === 'back') return prev > 0 ? prev - 1 : prev;
      if (direction === 'next') {
        const voted = Object.keys(votes[currentStatement?.ID] || {}).length > 0;
        if (config.requireVoteToContinue && !voted) return prev; // Hinweis s. UI
        return prev + 1;
      }
      return prev;
    });
  }, [votes, currentStatement, config]);

  // Pfeiltasten: links = vorige Frage, rechts = naechste Frage (nicht im Edit-Modus,
  // nicht waehrend Texteingabe, nicht auf der Auswertung)
  useEffect(() => {
    const onKey = (e) => {
      if (!started || atStats || isEditing) return;
      const tag = e.target && e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigate('back');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigate('next');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [started, atStats, isEditing, navigate]);

  // Zeitmessung fuer den 5-Minuten-Spam-Schutz beim E-Mail-Versand
  const startedAtRef = useRef(null);
  const startSession = useCallback(() => {
    if (!startedAtRef.current) startedAtRef.current = Date.now();
    setStarted(true);
  }, []);

  const resetAndRestart = useCallback(() => {
    setVotes({});
    setFlipped({});
    setShuffled({});
    setCurrentPos(0);
    setStarted(false);
    startedAtRef.current = null; // neue Runde -> Zeitzaehler neu
  }, []);

  // Logo-Klick: zurueck zu Frage 1 (Bewertungen bleiben erhalten)
  const goFirst = useCallback(() => {
    setStarted(true);
    setCurrentPos(0);
    window.scrollTo({ top: 0 });
  }, []);

  // Ergebnis per E-Mail (Endpunkt /api/send-result, SMTP per Env konfiguriert)
  const [mailEmail, setMailEmail] = useState('');
  const [mailName, setMailName] = useState('');
  const [mailStatus, setMailStatus] = useState(null); // {kind:'ok'|'err'|'info', msg}
  // Tick, damit das Mail-Formular nach Ablauf der 5 Minuten ohne User-Aktion erscheint
  const [, setMailTick] = useState(0);
  const sendResultMail = useCallback(async (email, payload, name) => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMailStatus({ kind: 'err', msg: 'Bitte eine gültige E-Mail-Adresse eingeben.' });
      return;
    }
    if (!name.trim()) {
      setMailStatus({ kind: 'err', msg: 'Bitte deinen Namen angeben.' });
      return;
    }
    setMailStatus({ kind: 'info', msg: 'PDF wird erstellt und gesendet …' });
    try {
      const res = await fetchWithErrorHandling('/api/send-result', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name,
          elapsed: Math.round((Date.now() - (startedAtRef.current || Date.now())) / 1000),
          pdf: payload,
        }),
      });
      setMailStatus(res && res.ok
        ? { kind: 'ok', msg: res.message || 'Ergebnis versendet.' }
        : { kind: 'err', msg: (res && res.message) || 'Versand fehlgeschlagen.' });
    } catch (e) {
      console.error('send-result:', e);
      setMailStatus({ kind: 'err', msg: 'Versand fehlgeschlagen – bitte später erneut versuchen.' });
    }
  }, []);

  // Mail-Spam-Schutz: Versand erst nach 5 Minuten; auf der Auswertung ticken,
  // damit das Formular puenktlich erscheint (ohne dass der User etwas tut)
  const elapsedMs = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
  const mailAllowed = startedAtRef.current !== null && elapsedMs >= 5 * 60 * 1000;
  useEffect(() => {
    if (!started || !atStats || mailAllowed) return undefined;
    const iv = setInterval(() => setMailTick((t) => t + 1), 5000);
    return () => clearInterval(iv);
  }, [started, atStats, mailAllowed]);

  const getSentimentColor = useCallback((voteType) => {
    if (voteType === 'up') return config.colorPalette.sentimentColors.up;
    if (voteType === 'neutral') return config.colorPalette.sentimentColors.neutral;
    if (voteType === 'down') return config.colorPalette.sentimentColors.down;
    return '';
  }, [config]);

  // ----------------------------- Edit-Modus -----------------------------

  const saveConfig = useCallback(async (updatedConfig) => {
    await fetchWithErrorHandling('/api/save-config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedConfig),
    });
    setConfig(updatedConfig);
  }, []);

  const handleTextChange = useCallback(async (key, value) => {
    const updated = { ...config, textDefinitions: { ...config.textDefinitions, [key]: value } };
    await saveConfig(updated);
  }, [config, saveConfig]);

  const handleCheckboxChange = useCallback(async (id) => {
    const idx = config.statementIndicesToShow.indexOf(id);
    const updated = { ...config };
    if (idx > -1) {
      updated.statementIndicesToShow = config.statementIndicesToShow.filter((x) => x !== id);
    } else {
      updated.statementIndicesToShow = [...config.statementIndicesToShow, id];
    }
    await saveConfig(updated);
  }, [config, saveConfig]);

  // Parteien-Filter: einzelne Parteien ein-/ausblenden (mindestens eine bleibt)
  const handlePartyToggle = useCallback(async (party) => {
    const list = config.partiesToShow && config.partiesToShow.length
      ? [...config.partiesToShow]
      : [...partyNames];
    const has = list.includes(party);
    if (has && list.length <= 1) return; // letzte Partei nicht abwaehlbar
    const next = has ? list.filter((p) => p !== party) : [...list, party];
    await saveConfig({ ...config, partiesToShow: next });
  }, [config, partyNames, saveConfig]);

  const saveData = useCallback(async (updatedData) => {
    await fetchWithErrorHandling('/api/save-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedData),
    });
    setAllData(updatedData);
  }, []);

  const handleStatementChange = useCallback(async (id, field, value) => {
    const updated = allData.map((s) => {
      if (s.ID !== id) return s;
      if (field.startsWith('Responses.')) {
        const [, resIdx, resField] = field.split('.');
        const responses = s.Responses.map((r, i) =>
          i === Number(resIdx) ? { ...r, [resField]: value } : r
        );
        return { ...s, Responses: responses };
      }
      return { ...s, [field]: value };
    });
    await saveData(updated);
  }, [allData, saveData]);

  // Editierbare Textfelder (einfache Inline-Editoren fuer Redaktion)
  const [editField, setEditField] = useState(null); // z.B. 'cfg::Text' oder '12::Responses.3.Comment'
  const [draft, setDraft] = useState('');

  const startEdit = (field, currentValue) => {
    setEditField(field);
    setDraft(currentValue);
  };
  const cancelEdit = () => {
    setEditField(null);
    setDraft('');
  };
  const commitEdit = async (statementId, field) => {
    if (statementId === 'cfg') {
      await handleTextChange(field, draft);
    } else {
      await handleStatementChange(statementId, field, draft);
    }
    setEditField(null);
    setDraft('');
  };

  // Konfig-Text (textDefinitions) editierbar machen — statementId === 'cfg'
  const renderEditableConfigText = (key) =>
    renderEditable('cfg', key, config.textDefinitions[key]);

  const renderEditable = (statementId, field, value, extraClass = '') => {
    const key = `${statementId}::${field}`;
    if (editField === key) {
      return (
        <span className="flex items-start gap-1 w-full">
          <textarea
            className={`border p-2 w-full bg-yellow-50 ${extraClass}`}
            rows={config.editModeTextBoxRows || 4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button className="ml-1" title="Speichern" onClick={() => commitEdit(statementId, field)}>💾</button>
          <button className="ml-1" title="Abbrechen" onClick={cancelEdit}>✖️</button>
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 w-full">
        <span className={extraClass}>{value}</span>
        <button className="ml-1" title="Bearbeiten" onClick={() => startEdit(key, value)}>✏️</button>
      </span>
    );
  };

  // ----------------------------- Rendering -----------------------------

  const renderStartPage = () => (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-7rem)] py-6">
      <img
        src={config.imagePaths.institutionLogo}
        alt="Institutionslogo"
        className="mb-3 w-44 sm:w-56 md:w-64 h-auto max-h-[24vh] object-contain"
      />
      <h1 className="text-base sm:text-lg text-center mb-4 max-w-2xl leading-relaxed">
        {isEditing
          ? renderEditableConfigText('willkommenText')
          : config.textDefinitions.willkommenText}
      </h1>
      <button className="bg-blue-500 text-white font-bold py-2 px-6 rounded text-lg" onClick={startSession}>
        Start
      </button>
    </div>
  );

  const renderStatement = () => {
    if (!currentStatement) return null;
    const id = currentStatement.ID;
    const qStr = String(id + 1).padStart(2, '0');
    const isQuestionPlaying = playingKey === `q:${id}`;
    // Antworten mit Original-Index (fuer Audio-Dateinamen q<Q>_<origIdx>.mp3)
    const numbered = currentStatement.Responses.map((response, oi) => ({ response, origIdx: oi }));
    // Parteien-Filter: im Spiel nur eingeschlossene Parteien; Edit zeigt alle
    const pool = isEditing
      ? numbered
      : numbered.filter((x) => isPartyShown(config, x.response.Party));
    // Edit-Modus: Original-Reihenfolge (stabile Felder fuer Redaktion);
    // Spiel-Modus: einmalig gemischte Karten (je Frage + Filterstand gecacht).
    const toCard = (x, i) => ({
      response: x.response,
      origIdx: x.origIdx,
      color: config.colorPalette.pastelBackground[i % config.colorPalette.pastelBackground.length],
    });
    const responsesWithColors = isEditing
      ? pool.map(toCard)
      : shuffled[`${id}::${partySig}`] || pool.map(toCard);
    const votedCount = Object.keys(votes[id] || {}).length;

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="col-span-full text-center">
          {isEditing && (
            <div className="text-sm text-gray-500">
              <label className="inline-flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.statementIndicesToShow.includes(id)}
                  onChange={() => handleCheckboxChange(id)}
                />
                In der Auswahl anzeigen
              </label>
            </div>
          )}
          {isEditing ? (
            renderEditable(id, 'Label', currentStatement.Label, 'text-base font-bold')
          ) : (
            <h2 className="text-base font-bold mb-1">{currentStatement.Label}</h2>
          )}
          <p className="text-xl md:text-2xl font-semibold mb-2">
            {isEditing
              ? renderEditable(id, 'Text', currentStatement.Text)
              : currentStatement.Text}
          </p>
          {!isEditing && (
            <button
              onClick={() => toggleAudio(`q:${id}`, `/audio/q${qStr}_q.mp3`)}
              className="inline-flex items-center gap-1 text-sm px-2 py-1 mb-2 border border-gray-300 rounded-full hover:bg-gray-100"
              title={isQuestionPlaying ? 'Stopp' : 'Frage vorlesen'}
              aria-pressed={isQuestionPlaying}
            >
              <span aria-hidden="true">{isQuestionPlaying ? '⏹' : '🔊'}</span>
              <span>{isQuestionPlaying ? 'Stopp' : 'Frage vorlesen'}</span>
            </button>
          )}
          {currentStatement.Infocheck && !isEditing && (
            <p className="text-xs text-gray-500 italic mb-1">{config.textDefinitions.infocheckHint}</p>
          )}
          {!isEditing && currentStatement.Source && (
            <p className="text-xs text-gray-400 mb-2">
              <a href={currentStatement.Source} target="_blank" rel="noreferrer" className="underline">
                {config.textDefinitions.sourceHint}
              </a>
            </p>
          )}
          {config.requireVoteToContinue && votedCount === 0 && (
            <p className="text-xs text-amber-600 mb-1">{config.textDefinitions.votePrompt}</p>
          )}
        </div>

        {responsesWithColors.map(({ response, origIdx, color }, idx) => {
          const voteType = votes[id]?.[response.Party];
          const isFlipped = flipped[id]?.[response.Party] || false;
          const sentimentColor = getSentimentColor(voteType);
          const logoPath = config.imagePaths.logos[response.Party];
          const audioUrl = `/audio/q${String(id + 1).padStart(2, '0')}_${String(origIdx).padStart(2, '0')}.mp3`;
          const audioKey = `${id}:${origIdx}`;
          const isPlaying = playingKey === audioKey;
          return (
            <ReactCardFlip key={`${id}-${response.Party}-${idx}`} isFlipped={isFlipped} flipDirection="horizontal">
              {/* Vorderseite: ANONYME Antwort + Bewertung (Partei wird erst
                  nach dem Flip gezeigt); nur im Edit-Modus als Hilfe sichtbar */}
              <div
                data-party={response.Party}
                aria-hidden={isFlipped ? true : undefined}
                className={`relative flex flex-col items-center justify-between p-4 rounded-lg shadow-md`}
                style={{ minHeight: '240px', backgroundColor: color }}
              >
                {!isEditing && (
                  <button
                    onClick={() => toggleAudio(audioKey, audioUrl)}
                    disabled={isFlipped}
                    aria-label={isPlaying ? 'Stopp' : 'Antwort anhören'}
                    aria-pressed={isPlaying}
                    className="absolute top-2 right-2 z-10 p-1 text-2xl md:text-3xl leading-none opacity-60 hover:opacity-100 transition-opacity disabled:opacity-30"
                    title={isPlaying ? 'Stopp' : 'Anhören'}
                  >
                    <span aria-hidden="true">{isPlaying ? '⏹' : '🔊'}</span>
                    <span className="sr-only">{isPlaying ? 'Stopp' : 'Antwort anhören'}</span>
                  </button>
                )}
                {isEditing && (
                  <div className="flex items-center justify-center gap-2 w-full mb-1">
                    <PartyLogo party={response.Party} logoPath={logoPath} sizeClass="h-6 w-6" />
                    <span className="font-bold text-xs text-gray-500">{response.Party}</span>
                  </div>
                )}
                <div className={`text-sm leading-relaxed w-full text-center overflow-y-auto flex-grow py-1 ${!isEditing ? 'pr-10' : ''}`} style={{ maxHeight: '180px' }}>
                  {isEditing
                    ? renderEditable(id, `Responses.${idx}.Comment`, response.Comment)
                    : anonymizeFrontText(response.Comment)}
                </div>
                <div className="flex mt-2 justify-around w-full items-center" style={{ padding: `${config.buttonPadding}px` }}>
                  <button onClick={() => handleVote(response.Party, 'up')} disabled={isFlipped} aria-label={config.textDefinitions.tooltipUp} className="mx-1 p-1 hover:scale-110 transition-transform disabled:opacity-40" title={config.textDefinitions.tooltipUp}>
                    <img src={config.imagePaths.votingIcons.up} alt="" className="w-6 h-6 md:w-10 md:h-10" />
                  </button>
                  <button onClick={() => handleVote(response.Party, 'neutral')} disabled={isFlipped} aria-label={config.textDefinitions.tooltipNeutral} className="mx-1 p-1 hover:scale-110 transition-transform disabled:opacity-40" title={config.textDefinitions.tooltipNeutral}>
                    <img src={config.imagePaths.votingIcons.neutral} alt="" className="w-7 h-7 md:w-11 md:h-11" />
                  </button>
                  <button onClick={() => handleVote(response.Party, 'down')} disabled={isFlipped} aria-label={config.textDefinitions.tooltipDown} className="mx-1 p-1 hover:scale-110 transition-transform disabled:opacity-40" title={config.textDefinitions.tooltipDown}>
                    <img src={config.imagePaths.votingIcons.down} alt="" className="w-6 h-6 md:w-10 md:h-10" />
                  </button>
                </div>
              </div>
              {/* Rueckseite: Stimmung + Partei + Zuruecknehmen */}
              <div
                data-party={response.Party}
                aria-hidden={isFlipped ? undefined : true}
                className="relative flex flex-col items-center justify-center p-4 rounded-lg shadow-md"
                style={{ minHeight: '240px', backgroundColor: sentimentColor }}
              >
                <PartyLogo party={response.Party} logoPath={logoPath} sizeClass="h-14 w-14 mb-2" />
                <div className="font-bold text-lg text-center text-white drop-shadow">{response.Party}</div>
                <div className="absolute inset-x-0 bottom-0 flex justify-center pb-2">
                  <button onClick={() => undoVote(response.Party)} disabled={!isFlipped} aria-label="Stimme zurücknehmen" className="mx-1 p-1 disabled:opacity-40" title={config.textDefinitions.tooltipReset}>
                    <img src={config.imagePaths.votingIcons.return} alt="" className="w-6 h-6 md:w-9 md:h-9" />
                  </button>
                </div>
              </div>
            </ReactCardFlip>
          );
        })}
      </div>
    );
  };

  const renderStatistics = () => {
    let stats = [];
    Object.entries(votes).forEach(([, partyVotes]) => {
      Object.entries(partyVotes).forEach(([party, value]) => {
        if (!stats.some((s) => s.name === party)) {
          stats.push({ name: party, Zustimmung: 0, Neutral: 0, Ablehnung: 0 });
        }
        const partyStat = stats.find((s) => s.name === party);
        if (value === 'up') partyStat.Zustimmung += 1;
        else if (value === 'neutral') partyStat.Neutral += 1;
        else if (value === 'down') partyStat.Ablehnung += 1;
      });
    });
    stats.forEach((stat) => {
      stat.Nettoergebnis = stat.Zustimmung - stat.Ablehnung;
    });
    stats.sort((a, b) => b.Nettoergebnis - a.Nettoergebnis);

    const podium = stats.slice(0, 3);
    const otherStats = stats.slice(3);
    const answeredCount = Object.keys(votes).length;
    // Prozent-Label je Kuchensegment (nur wenn Wert > 0)
    const pctLabel = (e) => (!e || !e.value || e.value === 0) ? '' : `${Math.round((e.percent || 0) * 100)}%`;

    const handleMailSubmit = (ev) => {
      ev.preventDefault();
      // Nur bewertete Fragen, darin nur bewertete Parteien (Reihenfolge = erlebt)
      const questions = list
        .filter((st) => votes[st.ID] && Object.keys(votes[st.ID]).length > 0)
        .map((st) => ({
          num: st.ID + 1,
          topic: st.Label || '',
          question: st.Text,
          entries: st.Responses
            .filter((r) => votes[st.ID] && votes[st.ID][r.Party])
            .map((r) => ({ party: r.Party, vote: votes[st.ID][r.Party], answer: anonymizeFrontText(r.Comment) })),
        }));
      const ranking = stats.map((s) => ({
        party: s.name,
        netto: s.Nettoergebnis,
        up: s.Zustimmung,
        neutral: s.Neutral,
        down: s.Ablehnung,
      }));
      sendResultMail(mailEmail, {
        questions,
        ranking,
      }, mailName);
    };

    return (
      <div className="mt-4">
        <h2 className="text-lg font-bold text-center">
          {isEditing ? renderEditableConfigText('statsPageTitle') : config.textDefinitions.statsPageTitle}
        </h2>
        <p className="text-center mb-4">
          {isEditing ? renderEditableConfigText('statsText') : config.textDefinitions.statsText}
        </p>
        {stats.length === 0 && (
          <p className="text-center text-gray-500">Noch keine Bewertungen abgegeben.</p>
        )}
        {stats.length > 0 && (
          <p className="text-center text-sm text-gray-500 -mt-2 mb-2">Bewertete Fragen: {answeredCount}</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 my-4 text-center">
          {podium.map((partyStat, idx) => (
            <div key={partyStat.name} className="p-4 m-2 bg-white rounded shadow flex flex-col items-center justify-center">
              <h3 className="text-md font-semibold">{`${idx + 1}. Platz: ${partyStat.name}`}</h3>
              <p className="text-sm">{`Nettoergebnis: ${partyStat.Nettoergebnis}`}</p>
              <ResponsiveContainer width="100%" height={200 * (1 - idx * 0.1)}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Zustimmung', value: partyStat.Zustimmung },
                      { name: 'Neutral', value: partyStat.Neutral },
                      { name: 'Ablehnung', value: partyStat.Ablehnung },
                    ]}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    outerRadius={80 * (1 - idx * 0.1)}
                    fill="#82ca9d"
                    label={pctLabel}
                    labelLine={false}
                  >
                    <Cell key="Zustimmung" fill="#82ca9d" />
                    <Cell key="Neutral" fill="#888888" />
                    <Cell key="Ablehnung" fill="#ff6347" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
        {otherStats.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-center">
            {otherStats.map((partyStat) => (
              <div key={partyStat.name} className="p-4 m-2 bg-white rounded shadow flex flex-col items-center justify-center">
                <h3 className="text-md font-semibold">{partyStat.name}</h3>
                <p className="text-sm">{`Nettoergebnis: ${partyStat.Nettoergebnis}`}</p>
                <ResponsiveContainer width="100%" height={100}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Zustimmung', value: partyStat.Zustimmung },
                        { name: 'Neutral', value: partyStat.Neutral },
                        { name: 'Ablehnung', value: partyStat.Ablehnung },
                      ]}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      outerRadius={40}
                      fill="#82ca9d"
                      label={pctLabel}
                      labelLine={false}
                    >
                      <Cell key="Zustimmung" fill="#82ca9d" />
                      <Cell key="Neutral" fill="#888888" />
                      <Cell key="Ablehnung" fill="#ff6347" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-center mt-4">
          <button className="bg-gray-500 text-white py-2 px-4 rounded" onClick={() => navigate('back')}>
            Zurück
          </button>
        </div>
        {!isEditing && stats.length > 0 && mailAllowed && (
          <div className="max-w-md mx-auto mt-6 border-t border-gray-200 pt-4 text-center">
            <h3 className="text-sm font-semibold mb-2">Ergebnis per E-Mail erhalten</h3>
            <form onSubmit={handleMailSubmit} className="flex flex-col gap-2">
              <div className="flex gap-2">
                <label htmlFor="result-name" className="sr-only">Dein Name</label>
                <input
                  id="result-name"
                  type="text"
                  required
                  value={mailName}
                  onChange={(e) => setMailName(e.target.value)}
                  placeholder="Dein Name"
                  maxLength={80}
                  className="border rounded px-2 py-1 text-sm flex-1 min-w-0"
                />
                <label htmlFor="result-email" className="sr-only">E-Mail-Adresse</label>
                <input
                  id="result-email"
                  type="email"
                  required
                  value={mailEmail}
                  onChange={(e) => setMailEmail(e.target.value)}
                  placeholder="deine@email.de"
                  className="border rounded px-2 py-1 text-sm flex-1 min-w-0"
                />
              </div>
              <button type="submit" className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 self-center">
                Senden
              </button>
            </form>
            {mailStatus && (
              <p
                role="status"
                className={`mt-2 text-sm ${mailStatus.kind === 'ok' ? 'text-green-700' : mailStatus.kind === 'err' ? 'text-red-600' : 'text-gray-500'}`}
              >
                {mailStatus.msg}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  if (error) {
    return <div className="text-red-500 text-center mt-10">{error}</div>;
  }

  if (!config) {
    return <div>Loading...</div>;
  }

  if (started && allData.length === 0) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-white focus:p-2 focus:border">
        Zum Inhalt springen
      </a>
      {/* Feste Kopfzeile: Logo + Titel + Schriftgroesse */}
      <header className="fixed top-0 inset-x-0 z-30 bg-white shadow-sm border-b border-gray-200 relative">
        <div className="container mx-auto flex items-center gap-1 sm:gap-2 px-2 sm:px-4 h-14">
          <button
            type="button"
            onClick={goFirst}
            aria-label="Zurück zur ersten Frage (Wahl bleibt erhalten)"
            title="Zurück zur ersten Frage"
            className="flex-none rounded hover:opacity-80"
          >
            <img src={config.imagePaths.institutionLogo} alt="" className="h-10 w-auto" />
          </button>
          <span className="font-bold text-sm sm:text-base md:text-lg truncate flex-1 min-w-0">
            {config.textDefinitions.headerTitle}
          </span>
          {started && list.length > 0 && !atStats && (
            <span aria-live="polite" className="text-xs sm:text-sm text-gray-600 whitespace-nowrap flex-none">
              Frage {Math.min(currentPos + 1, list.length)} von {list.length}
            </span>
          )}
          {started && (
            <button
              onClick={resetAndRestart}
              aria-label="Wahl löschen und neu anfangen"
              title="Wahl löschen und neu anfangen"
              className="flex-none bg-red-500 text-white text-xs sm:text-sm px-2 py-1 rounded hover:bg-red-600"
            >
              <span aria-hidden="true" className="sm:hidden">↺</span>
              <span className="hidden sm:inline">Wahl neu beginnen</span>
            </button>
          )}
          <div className="flex items-center gap-1 flex-none">
            <button
              aria-label="Schrift verkleinern"
              onClick={() => bumpFont(-10)}
              disabled={fontPct <= 80}
              className="border border-gray-300 rounded px-1.5 py-1 text-sm font-bold hover:bg-gray-100 disabled:opacity-40"
            >A−</button>
            <span aria-hidden="true" className="hidden sm:inline text-xs text-gray-500 w-8 text-center">{fontPct}%</span>
            <button
              aria-label="Schrift vergrößern"
              onClick={() => bumpFont(10)}
              disabled={fontPct >= 150}
              className="border border-gray-300 rounded px-1.5 py-1 text-sm font-bold hover:bg-gray-100 disabled:opacity-40"
            >A+</button>
          </div>
        </div>
        {/* Schmaler roter Fortschrittsbalken (Anteil der durchlaufenen Fragen) */}
        <div aria-hidden="true" className="absolute bottom-0 left-0 h-1 bg-red-600 transition-all duration-300" style={{ width: `${progressPct}%` }} />
      </header>
      <main id="main" className="container mx-auto px-4 pb-8" style={{ paddingTop: '4.5rem' }}>
        {/* Screenreader-Ansagen */}
        <div aria-live="polite" className="sr-only">{announce}</div>
        {/* Ein gemeinsamer Audio-Player fuer alle Karten (nur ein Ton gleichzeitig) */}
        <audio ref={audioRef} preload="none" onEnded={() => setPlayingKey(null)} onError={() => setPlayingKey(null)} />
        {!started ? (
        renderStartPage()
      ) : (
        <>
          <div className="flex items-start">
            <div className="flex-grow min-w-0">
              {!atStats ? (
                <>
                  {renderStatement()}
                  <div className="flex justify-between my-4">
                    <button className="bg-gray-500 text-white py-2 px-4 rounded" onClick={() => navigate('back')}>
                      {currentPos > 0 ? 'Zurück' : 'Zum Start'}
                    </button>
                    {(!config.requireVoteToContinue || Object.keys(votes[currentStatement?.ID] || {}).length > 0) && (
                      <button className="bg-blue-500 text-white py-2 px-4 rounded" onClick={() => navigate('next')}>
                        Weiter
                      </button>
                    )}
                  </div>
                </>
              ) : (
                renderStatistics()
              )}
            </div>
            {isEditing && (
              <div className="flex flex-col overflow-y-auto w-72 max-w-[85vw] shrink-0 ml-3">
                <WaterfallNavigation
                  statements={list}
                  currentPos={currentPos}
                  setCurrentPos={setCurrentPos}
                  indicesToShow={config.statementIndicesToShow}
                />
                <PartyFilter
                  parties={partyNames}
                  selected={(config.partiesToShow && config.partiesToShow.length)
                    ? config.partiesToShow
                    : partyNames}
                  onToggle={handlePartyToggle}
                />
              </div>
            )}
          </div>
        </>
      )}
      </main>
    </div>
  );
};

export default App;
